// @ts-nocheck
import React, { createContext, useContext, useReducer, type ReactNode, type Dispatch } from 'react';
import type { User, Workspace, Board, Group, Column, Item, CellValue, Folder } from './types';

export interface AppState {
  user: User | null;
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  folders: Folder[];
  boards: Board[];
  activeBoardId: string | null;
  groups: Group[];
  columns: Column[];
  items: Item[];
  cells: CellValue[];
  loading: boolean;
  sidebarCollapsed: boolean;
  boardRole: 'viewer' | 'editor' | 'admin' | null;
}

export type Action =
  | { type: 'SET_USER'; user: User | null }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_WORKSPACES'; workspaces: Workspace[] }
  | { type: 'SET_ACTIVE_WORKSPACE'; id: string }
  | { type: 'SET_FOLDERS'; folders: Folder[] }
  | { type: 'ADD_FOLDER'; folder: Folder }
  | { type: 'UPDATE_FOLDER'; id: string; data: Partial<Folder> }
  | { type: 'REMOVE_FOLDER'; id: string }
  | { type: 'SET_BOARDS'; boards: Board[] }
  | { type: 'ADD_BOARD'; board: Board }
  | { type: 'UPDATE_BOARD'; id: string; data: Partial<Board> }
  | { type: 'REMOVE_BOARD'; id: string }
  | { type: 'SET_ACTIVE_BOARD'; id: string | null }
  | { type: 'SET_BOARD_DATA'; groups: Group[]; columns: Column[]; items: Item[]; cells: CellValue[] }
  | { type: 'ADD_GROUP'; group: Group }
  | { type: 'UPDATE_GROUP'; id: string; data: Partial<Group> }
  | { type: 'REMOVE_GROUP'; id: string }
  | { type: 'ADD_COLUMN'; column: Column }
  | { type: 'UPDATE_COLUMN'; id: string; data: Partial<Column> }
  | { type: 'REMOVE_COLUMN'; id: string }
  | { type: 'ADD_ITEM'; item: Item }
  | { type: 'UPDATE_ITEM'; id: string; data: Partial<Item> }
  | { type: 'REMOVE_ITEM'; id: string }
  | { type: 'SET_CELL'; item_id: string; column_id: string; value: string }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'BULK_ADD_ITEMS'; items: Item[] }
  | { type: 'SET_BOARD_ROLE'; role: 'viewer' | 'editor' | 'admin' | null };

const initialState: AppState = {
  user: null,
  workspaces: [],
  activeWorkspaceId: null,
  folders: [],
  boards: [],
  activeBoardId: null,
  groups: [],
  columns: [],
  items: [],
  cells: [],
  loading: true,
  sidebarCollapsed: false,
  boardRole: null,
};

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.user };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_WORKSPACES':
      return { ...state, workspaces: action.workspaces };
    case 'SET_ACTIVE_WORKSPACE':
      return { ...state, activeWorkspaceId: action.id };
    case 'SET_FOLDERS':
      return { ...state, folders: action.folders };
    case 'ADD_FOLDER':
      return { ...state, folders: [...state.folders, action.folder] };
    case 'UPDATE_FOLDER':
      return { ...state, folders: state.folders.map(f => f.id === action.id ? { ...f, ...action.data } : f) };
    case 'REMOVE_FOLDER':
      return {
        ...state,
        folders: state.folders.filter(f => f.id !== action.id),
        boards: state.boards.map(b => b.folder_id === action.id ? { ...b, folder_id: null } : b),
      };
    case 'SET_BOARDS':
      return { ...state, boards: action.boards };
    case 'ADD_BOARD':
      return { ...state, boards: [...state.boards, action.board] };
    case 'UPDATE_BOARD':
      return { ...state, boards: state.boards.map(b => b.id === action.id ? { ...b, ...action.data } : b) };
    case 'REMOVE_BOARD':
      return { ...state, boards: state.boards.filter(b => b.id !== action.id) };
    case 'SET_ACTIVE_BOARD':
      return { ...state, activeBoardId: action.id };
    case 'SET_BOARD_DATA':
      return { ...state, groups: action.groups, columns: action.columns, items: action.items, cells: action.cells };
    case 'ADD_GROUP':
      return { ...state, groups: [...state.groups, action.group] };
    case 'UPDATE_GROUP':
      return { ...state, groups: state.groups.map(g => g.id === action.id ? { ...g, ...action.data } : g) };
    case 'REMOVE_GROUP':
      return {
        ...state,
        groups: state.groups.filter(g => g.id !== action.id),
        items: state.items.filter(i => i.group_id !== action.id),
      };
    case 'ADD_COLUMN':
      return { ...state, columns: [...state.columns, action.column] };
    case 'UPDATE_COLUMN':
      return { ...state, columns: state.columns.map(c => c.id === action.id ? { ...c, ...action.data } : c) };
    case 'REMOVE_COLUMN':
      return {
        ...state,
        columns: state.columns.filter(c => c.id !== action.id),
        cells: state.cells.filter(cv => cv.column_id !== action.id),
      };
    case 'ADD_ITEM':
      return { ...state, items: [...state.items, action.item] };
    case 'BULK_ADD_ITEMS':
      return { ...state, items: [...state.items, ...action.items] };
    case 'UPDATE_ITEM':
      return { ...state, items: state.items.map(i => i.id === action.id ? { ...i, ...action.data } : i) };
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(i => i.id !== action.id),
        cells: state.cells.filter(cv => cv.item_id !== action.id),
      };
    case 'SET_CELL': {
      const exists = state.cells.find(cv => cv.item_id === action.item_id && cv.column_id === action.column_id);
      if (exists) {
        return {
          ...state,
          cells: state.cells.map(cv =>
            cv.item_id === action.item_id && cv.column_id === action.column_id
              ? { ...cv, value: action.value }
              : cv
          ),
        };
      }
      return { ...state, cells: [...state.cells, { item_id: action.item_id, column_id: action.column_id, value: action.value }] };
    }
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'SET_BOARD_ROLE':
      return { ...state, boardRole: action.role };
    default:
      return state;
  }
}

const StateCtx = createContext<AppState>(initialState);
const DispatchCtx = createContext<Dispatch<Action>>(() => {});

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <StateCtx.Provider value={state}>
      <DispatchCtx.Provider value={dispatch}>
        {children}
      </DispatchCtx.Provider>
    </StateCtx.Provider>
  );
}

export function useAppState() { return useContext(StateCtx); }
export function useDispatch() { return useContext(DispatchCtx); }
